import PropTypes from 'prop-types';
import * as React from 'react';
import Drawer from '@base/Drawer/Drawer';
import H3 from '@base/H3/H3';
import { Spacings } from '@styles/index';
import styled from '@emotion/styled';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { segment } from '@actions/analytics';
import {
  GROUP_INVITE_CLICKED,
  PLANNER_PAGE,
  GROUP_JOIN_CLICKED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import Image from '@base/Image';
import { GroupsContext } from '@context/GroupsContext';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import DrawerScoreboardCard from './DrawerCards/DrawerScoreboardCard';
import GroupsInviteCard from './DrawerCards/GroupsInviteCard';
import GroupsChangeCard from './DrawerCards/GroupChangeCard';
import GroupStatusBlock from './DrawerCards/GroupStatusBlock';
import { groupsDrawerTypes } from './groupsConstants';
import GroupsJoinCard from './DrawerCards/GroupJoinCard';

const Wrapper = styled.div`
  grid-column: 1/10;
  padding-bottom: ${Spacings.SPACING_6B};
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IconWrapper = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  cursor: pointer;
  &:hover {
    background-color: ${({ isDarkModeOn }) =>
      isDarkModeOn ? 'var(--color-base-light-0)' : 'var(--color-i-gray-v2)'};
  }
`;

const GroupsActivityDrawer = ({
  drawerState,
  closeDrawerHandler,
  onChangeGroup,
  goalUID,
  showInviteCard,
  setShowInviteCard,
  type
}) => {
  const groupsContext = React.useContext(GroupsContext);

  const {
    startDate,
    endDate,
    currentDate,
    leaderboardData,
    competitionName,
    membersLimit,
    users: usersList
  } = groupsContext;

  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const currentUserId = useMyInfo(getMeUid);
  const goalSegmentData = useGoalEventProps({ goalUID });

  const onClickInvite = () => {
    setShowInviteCard((prevShowInviteCard) => !prevShowInviteCard);
    segment.track(GROUP_INVITE_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      group_size: usersList.length
    });
  };

  const redirectToPlayStore = () => {
    closeDrawerHandler();
    segment.track(GROUP_JOIN_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner'
    });

    window.open(
      'https://play.google.com/store/apps/details?id=com.unacademyapp'
    );
  };

  const renderDrawer = () => {
    switch (type) {
      case groupsDrawerTypes.CHANGE_GROUP:
        return (
          <GroupsChangeCard
            onCancel={closeDrawerHandler}
            onConfirm={onChangeGroup}
          />
        );
      case groupsDrawerTypes.JOIN_GROUP:
        return <GroupsJoinCard redirectToPlayStore={redirectToPlayStore} />;
      case groupsDrawerTypes.DEFAULT:
        return (
          <>
            <HeadingWrapper>
              <H3>Group</H3>
              {membersLimit > usersList.length && (
                <IconWrapper
                  onClick={onClickInvite}
                  isDarkModeOn={isDarkModeOn}
                >
                  <Image
                    height={Spacings.SPACING_6B}
                    width={Spacings.SPACING_6B}
                    src={`groups/add-person-${
                      isDarkModeOn ? 'light' : 'dark'
                    }.png`}
                    alt="unacademy"
                  />
                </IconWrapper>
              )}
            </HeadingWrapper>
            {showInviteCard && (
              <GroupsInviteCard
                inviteLink={
                  usersList.find((user) => user.uid === currentUserId)
                    ?.inviteLink
                }
              />
            )}
            <GroupStatusBlock
              usersList={usersList}
              currentUserId={currentUserId}
              closeDrawer={closeDrawerHandler}
              goalUID={goalUID}
            />
            <DrawerScoreboardCard
              startDate={startDate}
              endDate={endDate}
              currentDate={currentDate}
              goalUID={goalUID}
              competitionName={competitionName}
              leaderboard={leaderboardData}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      drawerState={drawerState}
      onClose={closeDrawerHandler}
      backgroundColor="var(--color-base-1)"
      className="Groups-DrawerPaper"
    >
      <Wrapper>{renderDrawer()}</Wrapper>
    </Drawer>
  );
};

export default GroupsActivityDrawer;

GroupsActivityDrawer.propTypes = {
  drawerState: PropTypes.bool.isRequired,
  closeDrawerHandler: PropTypes.func.isRequired,
  onChangeGroup: PropTypes.func,
  goalUID: PropTypes.string.isRequired,
  type: PropTypes.oneOf([...Object.keys(groupsDrawerTypes)])
};

GroupsActivityDrawer.defaultProps = {
  type: groupsDrawerTypes.DEFAULT,
  onChangeGroup: () => {}
};
